import React from 'react';
import { Layout } from '../layout';
import { TitleText, BodyText, StepIndicator, CardBody, Card } from '../ui';
import { useTheme } from '../../hooks/useTheme';
import { useReferralWorkflow } from '../../hooks/useReferralWorkflow';
import {
  PatientDetailsStep,
  DietitianSelectionStep,
  ReferralConfirmation
} from '../referral-workflow';
import { BackButton } from '../ui/BackButton';

interface ReferralWorkflowPageProps {
  className?: string;
}

// Step data for StepIndicator
const WORKFLOW_STEPS = [
  {
    key: 'step1',
    title: 'Patient Details',
    description: 'Enter patient\'s information',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
      </svg>
    )
  },
  {
    key: 'step2',
    title: 'Select Dietitian',
    description: 'Choose a dietitian',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
      </svg>
    )
  },
  {
    key: 'step3',
    title: 'Review & Submit',
    description: 'Review all details',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
    )
  }
];

/**
 * Main page component for the Provider Referral Workflow
 * Integrates with existing BiteWise Layout and theme system
 */
export const ReferralWorkflowPage: React.FC<ReferralWorkflowPageProps> = ({
  className = ''
}) => {
  const { isDark } = useTheme();
  const { state, actions, validation } = useReferralWorkflow();

  // Map current step number to step key
  const getCurrentStepKey = (stepNumber: number): string => {
    return `step${stepNumber}`;
  };


  const handleStepClick = (stepKey: string) => {
    // Convert step key back to number
    const stepNumber = parseInt(stepKey.replace('step', ''));
    const completedStepNumbers = [];
    if (validation.validateStep(1)) completedStepNumbers.push(1);
    if (validation.validateStep(2)) completedStepNumbers.push(2);

    if (completedStepNumbers.includes(stepNumber) || stepNumber === state.currentStep) {
      actions.setCurrentStep(stepNumber);
    }
  };

  const handleNext = () => {
    if (state.currentStep < 3) {
      actions.setCurrentStep(state.currentStep + 1);
    }
  };

  const handleBack = () => {
    if (state.currentStep > 1) {
      actions.setCurrentStep(state.currentStep - 1);
    }
  };

  const renderCurrentStep = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <PatientDetailsStep
            patientData={state.patientData}
            onDataChange={actions.updatePatientData}
            onNext={handleNext}
            onBack={handleBack}
            errors={state.errors}
            isLoading={state.isLoading}
            referralId='' // empty
          />
        );
      case 2:
        return (
          <DietitianSelectionStep
            dietitians={state.availableDietitians}
            selectedDietitian={state.selectedDietitian}
            filters={state.filters}
            onDietitianSelect={actions.selectDietitian}
            onFiltersChange={actions.updateFilters}
            onNext={handleNext}
            onBack={handleBack}
            isLoading={state.isLoading}
          />
        );
      case 3:
        return (
          <ReferralConfirmation
            patientData={state.patientData}
            selectedDietitian={state.selectedDietitian}
            onSubmit={actions.submitReferral}
            onBack={handleBack}
            isSubmitting={state.isSubmitting}
            submissionResult={state.submissionResult}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Layout className={`min-h-screen ${className}`}>
        {/* Page Header */}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center gap-4 mb-4">
              <BackButton fallback='/dashboard'/>
            </div>

            <TitleText>
              Patient Referral Workflow
            </TitleText>
            <BodyText
              variant="secondary"
              className="text-lg"
            >
              Refer your patients to qualified dietitians through our streamlined three-step process.
            </BodyText>
          </div>
        </div>

        {/* Main Content */}
        <main className={`
          flex-1
          ${isDark ? 'bg-secondary-900' : 'bg-white'}
        `}>
          <div className="container mx-auto px-4 py-4 sm:py-6">
            {/* Workflow Content */}
            <div className="max-w-6xl mx-auto">
              {/* Step Indicator */}
              <StepIndicator
                steps={WORKFLOW_STEPS}
                currentStep={getCurrentStepKey(state.currentStep)}
                onStepClick={handleStepClick}
                className="mb-8"
                variant="horizontal"
                size="md"
                ariaLabel="Referral workflow progress"
              />

              {/* Current Step Content */}
              <div className="flex justify-center">
                <Card className="w-full max-w-2xl shadow-lg">
                  <CardBody className="p-8">
                    {renderCurrentStep()}
                  </CardBody>
                </Card>
              </div>
            </div>
          </div>
        </main>
      </Layout>
    </>
  );
};